import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, catchError, of, switchMap, takeUntil } from "rxjs";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { PermissionService } from "src/app/shared/services/permission.service";
import { SessionService } from "src/app/shared/services/session.service";
import { IOrganizationDetails } from "../../domain/interfaces/organization.interface";
import { OrganizationService } from "../../domain/services/organization.service";

@Component({
  selector: "app-organization-page",
  templateUrl: "./organization-page.component.html",
  styleUrls: ["./organization-page.component.scss"],
})
export class OrganizationPageComponent implements OnInit, OnDestroy {
  public organizationDetails: IOrganizationDetails | null = null;

  private onDestroy$ = new Subject<void>();

  /**
   * Re-triggered every time loadOrganizationDetails() is called (including from
   * reloadOrganizationDetails()), so that switching organizations - or reloading the current one
   * - always cancels any still-pending request for a previous id.
   */
  private loadOrganizationId$ = new Subject<number>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly organizationService: OrganizationService,
    private readonly sessionService: SessionService,
    private readonly permissionService: PermissionService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService
  ) {}

  /**
   * Loads the organization details for the id in the current route, keeping them in sync if the
   * id changes.
   */
  ngOnInit(): void {
    // Cancels any still-pending getOrganizationDetailsById() request as soon as a newer one is
    this.loadOrganizationId$
      .pipe(
        switchMap((id) =>
          this.organizationService.getOrganizationDetailsById(id).pipe(
            catchError(() => {
              this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
                duration: 5000,
              });
              this.router.navigate(["/administration"]);
              return of(null);
            })
          )
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe((details) => {
        if (details) {
          this.organizationDetails = details;
        }
      });

    /**
     * Subscribed rather than read once from the snapshot: the id can change (e.g. navigating
     * from one organization's page directly to another), and the details must then be reloaded
     * from scratch for the new id.
     */
    this.route.paramMap.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      const id = Number(params.get("id"));

      if (!id) {
        this.router.navigate(["/administration"]);
        return;
      }

      this.loadOrganizationDetails(id);
    });
  }

  /**
   * Unsubscribes from all pending observables.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Whether the viewed organization is the one the current (SuperAdmin) session is scoped to:
   * only then does it make sense to offer inviting a member to it. Computed from the session's
   * current organization id rather than a one-off flag, so it stays correct even if the
   * session's current organization changes while this page is displayed.
   */
  get isCurrentOrganization(): boolean {
    return this.organizationDetails?.details.id === this.sessionService.currentOrgId;
  }

  /**
   * Whether the user's role allows inviting a member to the current organization
   */
  get canInviteMember(): boolean {
    return this.permissionService.isEnabled(Functionality.INVITE_MEMBER);
  }

  /**
   * Reloads the current organization's details, e.g. after a member has been invited, removed or
   * had their role changed, or an invitation has been resent or revoked.
   */
  reloadOrganizationDetails(): void {
    if (this.organizationDetails) {
      this.loadOrganizationDetails(this.organizationDetails.details.id);
    }
  }

  /**
   * Requests the given organization's details, redirecting back to the administration page on
   * failure. Does not clear the currently displayed details first, so a reload (e.g. after
   * inviting a member) does not flash the page blank while the fresh data is in flight. Routed
   * through loadOrganizationId$ (see ngOnInit) so a still-pending request for a previous id is
   * cancelled rather than racing with this one.
   * @param id The id of the organization to load
   */
  private loadOrganizationDetails(id: number): void {
    this.loadOrganizationId$.next(id);
  }
}
